import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { Edit, Trash2, Layers3, ChevronDown, Eye } from "lucide-react";
import { cn } from "../lib/cn";
import { nx, NX_RING_CLIP } from "../lib/onyx-material";
import { Card, CardCover, CardTitle, type CardProps } from "../primitives/Card";
import { Button } from "../primitives/Button";
import { VisibilityBadge } from "./VisibilityBadge";
import { Skeleton } from "../primitives/Skeleton";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../primitives/Tooltip";
import { AikoMark } from "./AikoMark";
import { Image } from "../primitives/Image";
import { Folder } from "./Folder";
import { Truncate } from "./Truncate";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "../primitives/DropdownMenu";

/**
 * CollectionCard — entity card for AI Studio Collections. Unique among the
 * three domain cards: instead of a cover image, the cover area renders an
 * animated `<Folder>` (adapted from React Bits) with the collection's first
 * three items as papers — cover thumbnails of experiences peek out of the
 * folder. Hovering nudges the papers up and skews the front flap; clicking
 * the folder fans the papers out for inspection.
 *
 * Click semantics:
 * - Folder is its own click target (toggles open/close, stops propagation)
 * - Clicking an open paper navigates to the collection (fires `onView`)
 * - Card body click (title / description / footer button) → `onView`
 *
 * AIKO whispers a contextual hint on title hover — pass `aikoTooltip={false}`
 * to opt out, or a custom string / node to override.
 *
 * Surface: composes the brand `Card`, so the Onyx hex-notch silhouette
 * (`nx-s`), the clip-safe `.elev-card` drop-shadow, and the continuous
 * `nx-rim` keyline ring are INHERITED — this shell adds no border/box-shadow
 * of its own (either would be sliced / clipped by the notch; #158). The grid
 * `overflow-visible` only lets the folder papers fan out past the cover; the
 * `nx-s` clip-path still shapes the card silhouette. `CollectionCard.Skeleton`
 * is hand-rolled (not via `Card`) so it applies the recipe directly: `nx-s`
 * + `.elev-card` + an `nx-rim` ring, never a CSS border or `box-shadow`.
 */

export interface CollectionItem {
  id?: string | number;
  title?: string;
  coverImage?: string;
}

export interface CollectionCardEntity {
  id: string | number;
  name: string;
  description?: string;
  coverImage?: string;
  studioName?: string;
  studioLogo?: string;
  itemCount: number;
  items?: CollectionItem[];
  visibility?: "public" | "private" | "team";
}

export interface CollectionCardProps extends Omit<
  CardProps,
  "onClick" | "loading" | "children" | "view" | "hover"
> {
  collection: CollectionCardEntity;
  view?: "grid" | "list";
  mode: "manage" | "explore" | "community";
  hover?: "lift" | "glow" | "none";

  /** Primary card action — also fires on whole-card click. */
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;

  /** AIKO hover-tooltip helper. Pass `false` to opt out. */
  aikoTooltip?: React.ReactNode | false;

  /**
   * Folder color (the front flap). Defaults to `hsl(var(--primary))`. Pass a
   * hex string or `null` to use the theme color.
   */
  folderColor?: string;
}

/**
 * Single paper rendered inside the Folder — uses pretty's `<Image>` primitive
 * so the experience thumbnail picks up consistent loading + error chrome.
 * Internal helper.
 */
function FolderPaper({ item }: { item: CollectionItem }) {
  if (item.coverImage) {
    return (
      <Image
        src={item.coverImage}
        alt={item.title ?? ""}
        rounded="none"
        className="h-full w-full"
      />
    );
  }
  return (
    <div className="h-full w-full flex items-center justify-center bg-muted/60 rounded-[inherit]">
      <Layers3 className="size-4 text-muted-foreground/60" />
    </div>
  );
}

/**
 * List-view thumbnail for CollectionCard — 2x2 mosaic when item thumbs are
 * available, otherwise a soft Layers3 fallback. Internal helper.
 */
function CollectionListThumb({ items }: { items: CollectionItem[] }) {
  const thumbs = items.filter((it) => it.coverImage).slice(0, 4);
  if (thumbs.length === 0) {
    return (
      <div className="relative h-full w-full overflow-hidden bg-gradient-to-br from-primary/15 via-accent/10 to-muted flex items-center justify-center">
        <Layers3 className="size-6 text-muted-foreground/70" strokeWidth={1.5} />
      </div>
    );
  }
  return (
    <div className="relative h-full w-full bg-muted">
      <div className="grid grid-cols-2 grid-rows-2 h-full w-full gap-0.5">
        {thumbs.map((it, i) => (
          <Image
            key={it.id ?? i}
            src={it.coverImage}
            alt=""
            rounded="none"
            className="h-full w-full"
          />
        ))}
        {Array.from({ length: 4 - thumbs.length }).map((_, i) => (
          <div
            key={`empty-${i}`}
            className="bg-gradient-to-br from-muted via-muted/60 to-muted/30 flex items-center justify-center"
          >
            <Layers3 className="size-3 text-muted-foreground/40" />
          </div>
        ))}
      </div>
    </div>
  );
}

function AikoTooltip({
  message,
  children,
}: {
  message: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <TooltipProvider delayDuration={400}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="flex items-start gap-2">
            <AikoMark size="xs" aria-hidden />
            <span className="text-xs leading-relaxed">{message}</span>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

type CollectionCardComponent = React.ForwardRefExoticComponent<
  CollectionCardProps & React.RefAttributes<HTMLDivElement>
> & {
  Skeleton: typeof CollectionCardSkeleton;
};

const CollectionCardImpl = React.forwardRef<HTMLDivElement, CollectionCardProps>(
  (
    {
      collection,
      view = "grid",
      mode,
      hover = "lift",
      onView,
      onEdit,
      onDelete,
      aikoTooltip,
      folderColor = "#0A2540",
      className,
      ...rest
    },
    ref,
  ) => {
    const { name, description, studioName, itemCount, items = [], visibility } = collection;
    const visKey = (visibility ?? "").toLowerCase();
    const showVisibility = Boolean(visKey);

    const aikoMessage =
      aikoTooltip === false
        ? null
        : (aikoTooltip ??
          `Open this collection — ${itemCount} ${itemCount === 1 ? "item" : "items"} curated${
            studioName ? ` in ${studioName}` : ""
          }.`);

    const stopProp = (e: React.MouseEvent) => e.stopPropagation();
    const fire = (handler?: () => void) => (e: React.MouseEvent | Event) => {
      if ("stopPropagation" in e) e.stopPropagation();
      handler?.();
    };

    const manageMenuItems: Array<React.ReactNode> = [];
    if (mode === "manage") {
      if (onView) {
        manageMenuItems.push(
          <DropdownMenuItem key="view" onSelect={() => onView()}>
            <Eye />
            <span>View</span>
          </DropdownMenuItem>,
        );
      }
      if (onDelete) {
        if (manageMenuItems.length > 0) manageMenuItems.push(<DropdownMenuSeparator key="sep" />);
        manageMenuItems.push(
          <DropdownMenuItem key="delete" variant="destructive" onSelect={() => onDelete()}>
            <Trash2 />
            <span>Delete</span>
          </DropdownMenuItem>,
        );
      }
    }

    // Track folder open state so the outer card can bump z-index above
    // siblings when papers fan out (otherwise they'd visually clip behind
    // adjacent cards in a dense grid).
    const [folderOpen, setFolderOpen] = React.useState(false);

    return (
      <Card
        ref={ref}
        view={view}
        hover={hover}
        onClick={onView}
        className={cn(
          view === "grid" && "h-full",
          // Grid view: outer Card overflow-visible so the folder's papers can
          // pop above the card's bordered frame on expand. List view stays
          // bounded (no folder there).
          view === "grid" && "overflow-visible",
          // When the folder is open we sit on top of sibling cards in the
          // grid so the fanned-out papers are never visually clipped.
          folderOpen && "z-30 relative",
          onView && "cursor-pointer",
          className,
        )}
        {...rest}
      >
        {view === "grid" ? (
          // ── Grid view: full-width 16:9 folder cover ──
          // The wrapper itself has NO overflow constraint; the gradient bg
          // lives on a separate absolutely-positioned child that owns the
          // top-rounded corner clipping. That decoupling lets the folder
          // layer extend above the cover bounds when papers fan out.
          <div className="relative w-full aspect-video">
            {/* Gradient background. The parent Card's `nx-s` clip-path shapes
                the visible top edge (top-left radius + the notch chamfer) even
                though grid view is `overflow-visible`; `rounded-t-xl` (the
                `nx-s` 12px radius) just keeps this inner layer's corner from
                poking past the silhouette during the brief pre-clip paint. */}
            <div
              aria-hidden
              className="absolute inset-0 rounded-t-xl overflow-hidden bg-gradient-to-br from-primary/10 via-accent/5 to-muted"
            >
              <div
                aria-hidden
                className="absolute -top-1/3 -left-1/4 size-[140%] rounded-full bg-white/30 blur-3xl dark:bg-white/5"
              />
              {/* Bottom shade for badge legibility — clipped inside the
                  gradient layer so it inherits the rounded top corners. */}
              <div
                aria-hidden
                className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/75 via-black/35 to-transparent"
              />
            </div>

            {/* Bottom-left strip — visibility only. The item-count chip used
                to live here too, but the centred Folder visual overlapped
                it; the count is still surfaced one row down in the meta
                strip below the title, so dropping the on-cover chip is
                a clean win. */}
            {showVisibility ? (
              <div className="absolute inset-x-3 bottom-3 z-10 pointer-events-none">
                <div
                  className={cn(
                    "flex items-center gap-2 overflow-x-auto pointer-events-auto",
                    "[scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
                    "[mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                    "[-webkit-mask-image:linear-gradient(to_right,black_0,black_calc(100%-2rem),transparent_100%)]",
                    "pr-8",
                  )}
                >
                  <VisibilityBadge
                    visibility={visKey}
                    appearance="overlay"
                    size="sm"
                    className="flex-shrink-0 shadow-sm"
                  />
                </div>
              </div>
            ) : null}

            {/* Folder layer — free to escape the cover bounds when open */}
            <div className="absolute inset-0 flex items-center justify-center pt-4 z-20">
              <Folder
                size={1.5}
                color={folderColor}
                items={items.slice(0, 3).map((it, i) => (
                  <FolderPaper key={it.id ?? i} item={it} />
                ))}
                onItemClick={() => onView?.()}
                onOpenChange={setFolderOpen}
              />
            </div>
          </div>
        ) : (
          // ── List view: small thumbnail mosaic on the left ──
          // List rows are tight by design — drop the dramatic folder visual
          // and use the CardCover thumbnail pattern that Studio + Experience
          // list cards already use, so the three list views read as one set.
          <CardCover
            view="list"
            aspect="16/9"
            src={undefined}
            alt={`${name || "Collection"} thumbnail`}
            fallback={<CollectionListThumb items={items} />}
          />
        )}

        {/* Content section. `flex-1` so it absorbs remaining card height
            (outer Card has `h-full` in grid view), keeping the action footer
            pinned to the bottom across siblings with varied content lengths. */}
        <div
          className={cn(
            "flex-1 flex flex-col gap-3 min-w-0",
            view === "grid" ? "p-4 sm:p-5" : "py-1 px-1",
          )}
        >
          {aikoMessage ? (
            <AikoTooltip message={aikoMessage}>
              <CardTitle
                as="h3"
                size={view === "grid" ? "md" : "sm"}
                clamp={view === "list" ? 1 : 2}
              >
                {name}
              </CardTitle>
            </AikoTooltip>
          ) : (
            <CardTitle as="h3" size={view === "grid" ? "md" : "sm"} clamp={view === "list" ? 1 : 2}>
              {name}
            </CardTitle>
          )}

          {/* Description — auto-tooltip on overflow via `<Truncate>`. */}
          <Truncate
            as="p"
            lines={view === "list" ? 1 : 2}
            className={cn(
              "text-sm leading-relaxed text-muted-foreground",
              view === "grid" && "min-h-[2.6rem]",
            )}
            tooltipBody={typeof description === "string" ? description : undefined}
          >
            {description ?? <span className="invisible">·</span>}
          </Truncate>

          {/* Meta row. `min-h-[1.25rem]` keeps the row at one line of height
              even when the content row wraps, so the action footer Y stays
              uniform across siblings. */}
          <div className="flex min-h-[1.25rem] items-center gap-x-3 gap-y-1 flex-wrap text-xs sm:text-sm text-muted-foreground">
            {studioName ? <Truncate as="span">{studioName}</Truncate> : null}
            <span className="inline-flex items-center gap-1 flex-shrink-0">
              <Layers3 className="size-3.5" />
              {itemCount} {itemCount === 1 ? "item" : "items"}
            </span>
          </div>

          {/* Action footer. `mt-auto` pins it to the bottom of the flex column. */}
          {view === "grid" ? (
            <div className="mt-auto flex items-center gap-2 pt-1" onClick={stopProp}>
              {mode === "manage" && onEdit ? (
                <>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={fire(onEdit)}
                    aria-label={`Edit ${name}`}
                  >
                    <Edit />
                    <span>Edit</span>
                  </Button>
                  {manageMenuItems.length > 0 ? (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          size="icon"
                          variant="outline"
                          className="size-9 flex-shrink-0"
                          aria-label={`${name || "Collection"} more actions`}
                        >
                          <ChevronDown />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">{manageMenuItems}</DropdownMenuContent>
                    </DropdownMenu>
                  ) : null}
                </>
              ) : mode !== "manage" && onView ? (
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={fire(onView)}
                  aria-label={`Open ${name}`}
                >
                  <span>Open</span>
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </Card>
    );
  },
);
CollectionCardImpl.displayName = "CollectionCard";

/* -------------------------------------------------------------------------- */
/* CollectionCard.Skeleton                                                    */
/* -------------------------------------------------------------------------- */

interface CollectionCardSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  view?: "grid" | "list";
}

export const CollectionCardSkeleton = React.forwardRef<HTMLDivElement, CollectionCardSkeletonProps>(
  ({ view = "grid", className, ...props }, ref) => {
    if (view === "list") {
      return (
        <div
          ref={ref}
          className={cn(
            // Mirror the Card surface recipe (#158): the `nx-s` notch clips
            // the silhouette, `.elev-card` is the clip-safe drop-shadow, and
            // the keyline below is an `nx-rim` ring — never a CSS border or
            // outer box-shadow (both are sliced / clipped by the notch).
            nx.s,
            "relative bg-card elev-card p-3 sm:p-4 flex items-start gap-4",
            className,
          )}
          {...props}
        >
          <span
            aria-hidden
            className="nx-rim pointer-events-none absolute inset-0 [--nx-rim:hsl(var(--border))]"
            style={{ clipPath: NX_RING_CLIP.s }}
          />
          <Skeleton
            animation="shimmer"
            className="aspect-video w-32 sm:w-40 lg:w-48 rounded-lg flex-shrink-0"
          />
          <div className="flex-1 min-w-0 flex flex-col gap-2 py-1">
            <Skeleton animation="shimmer" className="h-5 w-3/5 rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
            <Skeleton animation="shimmer" className="h-3 w-1/3 rounded-md" />
          </div>
        </div>
      );
    }
    return (
      <div
        ref={ref}
        className={cn(
          // Same surface recipe as the grid Card it stands in for: `nx-s`
          // notch silhouette + clip-safe `.elev-card` drop-shadow + an
          // `nx-rim` ring keyline. `overflow-hidden` lets the `nx-s` clip
          // shape the flush cover skeleton's top edge (incl. the chamfer).
          nx.s,
          "relative bg-card elev-card overflow-hidden flex flex-col",
          className,
        )}
        {...props}
      >
        <span
          aria-hidden
          className="nx-rim pointer-events-none absolute inset-0 z-[1] [--nx-rim:hsl(var(--border))]"
          style={{ clipPath: NX_RING_CLIP.s }}
        />
        <Skeleton animation="shimmer" className="aspect-video w-full rounded-none" />
        <div className="p-4 sm:p-5 flex flex-col gap-3">
          <Skeleton animation="shimmer" className="h-5 w-3/5 rounded-md" />
          <div className="flex flex-col gap-1.5">
            <Skeleton animation="shimmer" className="h-4 w-full rounded-md" />
            <Skeleton animation="shimmer" className="h-4 w-4/5 rounded-md" />
          </div>
          <Skeleton animation="shimmer" className="h-3 w-1/2 rounded-md" />
          <div className="flex items-center gap-2 pt-1">
            <Skeleton animation="shimmer" className="h-9 flex-1 rounded-full" />
            <Skeleton animation="shimmer" className="size-9 rounded-full" />
          </div>
        </div>
      </div>
    );
  },
);
CollectionCardSkeleton.displayName = "CollectionCard.Skeleton";

/* -------------------------------------------------------------------------- */
/* CollectionCardGrid                                                          */
/* -------------------------------------------------------------------------- */

export interface CollectionCardGridProps extends React.HTMLAttributes<HTMLDivElement> {
  items: CollectionCardEntity[];
  renderItem: (collection: CollectionCardEntity, index: number) => React.ReactNode;
  animate?: boolean;
  columns?: "2" | "3" | "4";
  loading?: boolean;
  skeletonCount?: number;
}

export const CollectionCardGrid = React.forwardRef<HTMLDivElement, CollectionCardGridProps>(
  (
    {
      items,
      renderItem,
      animate = true,
      columns = "3",
      className,
      loading = false,
      skeletonCount = 6,
      ...props
    },
    ref,
  ) => {
    const colClass =
      columns === "2"
        ? "grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6"
        : columns === "4"
          ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6"
          : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6";

    if (loading) {
      return (
        <motion.div
          ref={ref}
          className={cn(colClass, className)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          {...(props as React.ComponentProps<typeof motion.div>)}
        >
          {Array.from({ length: skeletonCount }).map((_, i) => (
            <CollectionCardSkeleton key={i} />
          ))}
        </motion.div>
      );
    }

    if (!animate) {
      return (
        <div ref={ref} className={cn(colClass, className)} {...props}>
          {items.map((c, i) => (
            <React.Fragment key={c.id}>{renderItem(c, i)}</React.Fragment>
          ))}
        </div>
      );
    }
    return (
      <div ref={ref} className={cn(colClass, className)} {...props}>
        <AnimatePresence mode="popLayout" initial={false}>
          {items.map((c, i) => (
            <motion.div
              key={c.id}
              layout
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{
                layout: { type: "spring", stiffness: 260, damping: 30 },
                opacity: { duration: 0.25, ease: "easeOut" },
                y: { duration: 0.25, ease: "easeOut" },
                delay: Math.min(i * 0.03, 0.18),
              }}
            >
              {renderItem(c, i)}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    );
  },
);
CollectionCardGrid.displayName = "CollectionCardGrid";

export const CollectionCard = CollectionCardImpl as CollectionCardComponent;
CollectionCard.Skeleton = CollectionCardSkeleton;
